/**
 * The Family Portal's model, built from the route answers: the design's FP_* records
 * (L35264-35357) become these fields, one route each. Nothing is invented: a block whose
 * route has not answered is empty, and the page draws the designed empty state.
 */
import type {
  FamilyCarePlanResponse,
  FamilyCircleAccessResponse,
  FamilyCoverageResponse,
  FamilyDashboardResponse,
  FamilyDatesResponse,
  FamilyDoctorSummaryResponse,
  FamilyInboxResponse,
  FamilyMomentsResponse,
  FamilyNoticesResponse,
  FamilyPortalResponse,
  FamilyThreadResponse,
  FamilyTodoResponse,
  FamilyVisitsResponse,
} from '@aithora/shared';
import { DASH, type FpModel } from './familyVals';

export interface FpSources {
  persona: 'Family' | 'Operator' | 'Facility Admin';
  viewerId: string | null;
  resident: { id: string; name: string; organizationId: string; room?: string | null; facilityName?: string | null; relationship?: string | null; atHome?: boolean } | null;
  notices: FamilyNoticesResponse | null;
  thread: FamilyThreadResponse | null;
  moments: FamilyMomentsResponse | null;
  dates: FamilyDatesResponse | null;
  circle: FamilyCircleAccessResponse | null;
  visits: FamilyVisitsResponse | null;
  plan: FamilyCarePlanResponse | null;
  doctor: FamilyDoctorSummaryResponse | null;
  portal: FamilyPortalResponse | null;
  coverage: FamilyCoverageResponse | null;
  inbox: FamilyInboxResponse | null;
  dashboard: FamilyDashboardResponse | null;
  photoSent: boolean;
  /** Why the last photo did not reach her Atlas, when it did not. */
  photoNote?: string | null;
  /** GET /family/todo (family only). */
  todo?: FamilyTodoResponse | null;
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY = 86400000;
/** "6:42am", "7pm" is written "7:00pm". */
const clock = (d: Date) => { const h = d.getHours(), m = d.getMinutes(); return (h % 12 || 12) + ':' + String(m).padStart(2, '0') + (h >= 12 ? 'pm' : 'am'); };
/** "Aug 14, 6:42am", or "Aug 12" when the time is not known (midnight). */
export const dateTime = (iso: string) => { const d = new Date(iso); const md = MON[d.getMonth()] + ' ' + d.getDate(); return d.getHours() === 0 && d.getMinutes() === 0 ? md : md + ', ' + clock(d); };
/** "Ruth K." from "Ruth Kim" (the design's name on a confirmation). */
export const shortName = (n: string | null | undefined) => {
  const w = String(n ?? '').trim().split(/\s+/).filter(Boolean);
  return w.length === 0 ? 'Someone' : w.length === 1 ? w[0] : w[0] + ' ' + w[w.length - 1][0] + '.';
};
/** A time as the design stamps a tap ("6:52 PM"). */
export const timeOf = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const dateOnly = (iso: string) => { const d = new Date(iso); return MON[d.getMonth()] + ' ' + d.getDate(); };
const dayStart = (ms: number) => { const d = new Date(ms); d.setHours(0, 0, 0, 0); return d.getTime(); };
/** "yesterday 7:05pm", "today 9:02am", "Aug 3 6:10pm". */
export const relDay = (iso: string, nowMs: number) => {
  const d = new Date(iso);
  const days = Math.round((dayStart(nowMs) - dayStart(d.getTime())) / DAY);
  return (days === 0 ? 'today' : days === 1 ? 'yesterday' : dateOnly(iso)) + ' ' + clock(d);
};
const WEEKDAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
/** "Yesterday 6:12pm", "Sunday 11:20pm" within the week, else "Sep 12 9:04am". */
export const eventWhen = (iso: string, nowMs: number) => {
  const d = new Date(iso);
  const days = Math.round((dayStart(nowMs) - dayStart(d.getTime())) / DAY);
  return (days === 0 ? 'Today' : days === 1 ? 'Yesterday' : days <= 7 ? WEEKDAY[d.getDay()] : dateOnly(iso)) + ' ' + clock(d);
};
const daysAgo = (iso: string, nowMs: number) => Math.max(0, Math.round((dayStart(nowMs) - dayStart(Date.parse(iso))) / DAY));
const lastShort = (iso: string | null, nowMs: number) => {
  if (!iso) return 'never';
  const n = daysAgo(iso, nowMs);
  return n === 0 ? 'today' : n < 14 ? n + (n === 1 ? ' day ago' : ' days ago') : Math.round(n / 7) + ' weeks ago';
};
/** A moment with no stored file yet shows a quiet tint, one hue per position. */
const TINT = [150, 80, 20, 250, 190, 320];
const tint = (i: number) => 'linear-gradient(135deg,oklch(0.80 0.05 ' + TINT[i % TINT.length] + '),oklch(0.62 0.07 ' + (TINT[i % TINT.length] + 30) + '))';
const CLASS: Record<string, string> = { fall: 'Accident or injury · 483.10(g)(14)', med_change: 'Medication change', hospital_transfer: 'Transfer or hospitalisation', doctor_visit: 'Doctor visit' };

export function buildModel(s: FpSources, nowMs: number): FpModel {
  const fam = s.persona === 'Family';
  const res = s.resident;
  const name = res?.name ?? DASH;
  const first = name.split(' ')[0];
  const initials = name.split(/\s+/).map((w) => w[0] ?? '').join('').slice(0, 2).toUpperCase();
  const members = s.circle?.members ?? [];
  const me = members.find((m) => m.userId === s.viewerId) ?? null;
  const approver = members.find((m) => m.role === 'approver') ?? members[0] ?? null;
  const selfSrc = fam ? me : approver;
  // Whose eyes the portal speaks through: the viewer for family, the primary contact for staff.
  const selfId = fam ? s.viewerId : approver?.userId ?? null;
  const relOf = (m: (typeof members)[number] | null) => (m?.relation ?? '').split(' · ')[0].toLowerCase();
  const self = { name: selfSrc?.name ?? (fam ? 'You' : 'the family'), rel: relOf(selfSrc) || (res?.relationship ?? '') };
  const nameOf = (id: string | null, fallback: string | null) => members.find((m) => m.userId === id)?.name ?? fallback ?? 'Someone';
  const relFor = (id: string | null) => relOf(members.find((m) => m.userId === id) ?? null);
  const p = s.portal;

  const notices = (s.notices?.notices ?? []).map((n) => {
    const channels = n.myChannels ?? n.seenBy.flatMap((x) => x.channels);
    const how = n.toldHow ?? (channels.includes('voice_call') ? 'Called you' : 'Portal notice');
    return {
      id: n.id,
      k: n.headline,
      when: n.occurredAt ? dateTime(n.occurredAt) : DASH,
      told: how + ' ' + (n.postedAt ? (n.toldHow ? dateOnly(n.postedAt) : dateTime(n.postedAt)) : DASH),
      by: n.by ?? DASH,
      cls: n.classLabel ?? CLASS[n.kind] ?? n.kind,
      sev: (n.severity ?? (n.kind === 'fall' || n.kind === 'hospital_transfer' ? 'high' : 'med')) as 'high' | 'med' | 'low',
      med: n.kind === 'med_change',
      mine: !!n.mySeenAt,
      kind: n.kind,
      seenBy: n.seenBy.map((x) => ({
        name: x.userId === selfId ? 'You' : (x.name ?? 'Someone').split(' ')[0], seen: x.seen,
        short: shortName(x.name), at: x.seenAt ? timeOf(x.seenAt) : null,
      })),
    };
  });

  const msgs = s.thread?.messages ?? [];
  const thread = msgs.map((m) => {
    const mine = m.senderRole === 'family' && (fam ? m.senderUserId === s.viewerId : m.senderUserId === approver?.userId);
    const who = m.senderRole === 'family' ? (m.senderUserId === selfId ? 'You' : m.senderName ?? 'Family') : m.senderName ?? 'Care team';
    return { mine, body: m.body, meta: who + ' · ' + (m.createdAt ? relDay(m.createdAt, nowMs) : DASH) };
  });
  const lastFamily = msgs.filter((m) => m.senderRole === 'family' && m.readAt).slice(-1)[0] ?? null;
  const threadSeen = lastFamily ? 'Seen by ' + (lastFamily.seenByName ?? 'the care team') + ' · ' + relDay(lastFamily.readAt!, nowMs) : '';

  const visitsNow = s.visits?.asOf ? Date.parse(s.visits.asOf) : nowMs;
  const visits = (s.visits?.visits ?? []).slice().sort((a, b) => Date.parse(b.visitedAt) - Date.parse(a.visitedAt)).map((v) => {
    const mine = v.visitorUserId === selfId;
    const who = nameOf(v.visitorUserId, v.visitorName).split(' ')[0];
    const rel = relFor(v.visitorUserId);
    const nm = nameOf(v.visitorUserId, v.visitorName);
    return {
      who: mine ? 'You' : (v.visitorUserId === approver?.userId ? nm : who) + (rel ? ' (' + rel + ')' : ''),
      when: dateOnly(v.visitedAt),
      days: daysAgo(v.visitedAt, visitsNow),
      note: v.note ?? (v.source === 'checkin' ? 'Checked in at the front desk' : 'Visit logged'),
      mine,
    };
  });

  const circle = members.map((m) => {
    const mine = m.userId === selfId;
    const rel = relOf(m);
    const shown = (mine ? 'You' : m.userId === approver?.userId && !fam ? m.name ?? 'Approver' : (m.name ?? 'Member').split(' ')[0]) + (rel ? ' · ' + rel : '');
    return {
      who: shown,
      rel: m.relation ?? DASH,
      role: m.role === 'approver' ? 'Approver' : 'Can view',
      state: 'VERIFIED' as 'VERIFIED' | 'INVITED',
      inviteId: null as string | null,
      sees: m.sees ?? (m.role === 'approver' ? 'Everything ' + first + ' agreed to share' : 'What ' + first + ' shares with the circle'),
      // What "never" measured, said: when each person last opened this portal. You are here now.
      last: mine ? 'you are here now' : m.lastViewedAt ? 'opened this portal ' + lastShort(m.lastViewedAt, nowMs) : 'has not opened the portal yet',
      lastShort: mine ? 'here now' : m.lastViewedAt ? 'opened ' + lastShort(m.lastViewedAt, nowMs) : 'never opened',
      mine,
    };
  }).concat((s.circle?.pendingInvites ?? []).map((iv) => ({
    who: (iv.name ?? 'Invitee').split(' ')[0] + (iv.relation ? ' · ' + iv.relation.toLowerCase() : ''),
    rel: iv.relation ?? DASH, role: 'Invited', state: 'INVITED' as const, sees: 'Nothing until she verifies',
    last: iv.sentAt ? 'invited ' + lastShort(iv.sentAt, nowMs) + ', not accepted' : 'invited, not accepted', lastShort: 'invited', mine: false, inviteId: iv.id,
  })));

  const goals = s.plan?.goals ?? [];
  const planGoals = goals.map((g) => ({
    goal: g.goalText, how: g.how ?? '', on: g.status === 'on_track',
    evidence: g.evidence ?? g.evidenceNote ?? 'Not started', basis: g.basis ?? (g.evidence ? 'MEASURED' : 'NOT OBSERVED'),
    source: g.evidenceSource ?? 'care record', pct: g.progressPct ?? 0, next: g.next ?? '', owner: g.owner ?? '',
  }));
  const conf = s.plan?.nextConference;
  const lastConf = s.plan?.lastConference;
  const md = (iso: string) => { const d = new Date(iso); return MON[d.getMonth()] + ' ' + d.getDate(); };
  const planMeeting = conf?.scheduledAt
    ? 'Next care conference: ' + md(conf.scheduledAt) + ', ' + clock(new Date(conf.scheduledAt)).replace(':00', '') + ', in person or by phone.' + (lastConf ? ' The last one was ' + md(lastConf.at) + (lastConf.attended ? ' and you attended.' : '.') : '')
    : 'No care conference is booked yet.';

  const dr = s.doctor;
  const mob = dr?.mobility;
  const meds = (dr?.medications.questions ?? []).map((q) => ({ name: q.medication, rule: q.rule ?? 'Worth asking', why: q.question }));
  // Relative counts run from the server's answer when it says when that was.
  const datesNow = s.dates?.asOf ? Date.parse(s.dates.asOf) : nowMs;
  const dates = (s.dates?.dates ?? []).map((x) => {
    const y = new Date(datesNow).getFullYear();
    let at = new Date(y, x.month - 1, x.day).getTime();
    if (at < dayStart(datesNow)) at = new Date(y + 1, x.month - 1, x.day).getTime();
    const inDays = Math.round((at - dayStart(datesNow)) / DAY);
    return {
      id: x.id, mon: MON[x.month - 1].toUpperCase(), day: String(x.day), what: x.label,
      inDays: inDays === 0 ? 'today' : 'in ' + inDays + (inDays === 1 ? ' day' : ' days'), note: x.note ?? '',
      remindResident: x.remindResident ?? x.remindWho === 'resident', remindMe: x.remindMe ?? x.remindWho === 'family', at,
    };
  }).sort((a, b) => a.at - b.at);

  const moments = (s.moments?.moments ?? []).map((m, i) => ({
    cap: m.caption ?? (m.source === 'family' ? 'Sent to ' + first : 'A moment'),
    who: m.createdBy?.userId && m.createdBy.userId === s.viewerId ? 'You' : m.createdBy?.name ?? (m.source === 'staff' ? 'Care team' : 'Family'),
    when: m.createdAt ? (daysAgo(m.createdAt, nowMs) === 0 ? 'Today ' + clock(new Date(m.createdAt)) : dateOnly(m.createdAt)) : DASH,
    bg: m.fileUrl ? 'url(' + m.fileUrl + ')' : tint(i),
  }));

  return {
    fam,
    role: s.persona,
    self,
    resident: res ? {
      id: res.id, name, first, initials,
      room: res.room ?? DASH,
      facility: res.facilityName ?? s.thread?.routedTo.name ?? DASH,
      atHome: !!res.atHome,
    } : null,
    notices,
    thread,
    threadSeen,
    threadsExtra: p?.threads ?? [],
    visits,
    circle,
    plan: planGoals,
    planMeeting,
    planGap: s.plan?.gapNote ?? (planGoals.some((g) => !g.on) ? 'A goal is not being met. Worth raising at the conference, or sooner.' : 'Every goal is on track.'),
    planReview: s.plan?.nextReviewAt ? 'Next plan review: ' + md(s.plan.nextReviewAt) : '',
    meds,
    moodSeries: moodFrom(s),
    fall: { score: mob?.available ? mob.score : null, conf: mob?.confidence ?? DASH },
    drivers: (mob?.drivers ?? []).map((label, i) => ({ label, w: mob?.driverWeights?.[i] ?? null })),
    healthSeries: mob?.series ?? [],
    healthNote: mob?.seriesNote ?? '14 days · grey is inside her own steady range',
    healthBasis: mob?.basis ?? [],
    healthAction: mob?.action ?? null,
    summaryLines: dr?.summaryLines ?? dr?.questionsToAsk ?? [],
    doctor: dr?.doctorName ?? 'her doctor',
    moments,
    dates,
    coming: p?.schedule ?? [],
    tasks: (p?.tasks ?? []).map((t) => ({ id: t.id, t: t.text, who: t.mine ? 'You' : t.who, mine: t.mine, done: t.done })),
    events: (p?.events ?? []).map((e) => ({ ...e, when: eventWhen(e.at, nowMs) })),
    panels: p?.panels ?? [],
    appt: p?.appointment ? { chain: p.appointment.chain, items: p.appointment.items } : null,
    stats: p?.stats ?? { visits30d: visits.filter((v) => v.days <= 30).length, checkInsWeek: null, openRequests: null },
    summary: p?.summary ?? '',
    careNote: p?.careNote?.text ?? '',
    careBy: p?.careNote ? p.careNote.by + ' · ' + (daysAgo(p.careNote.at, nowMs) === 0 ? 'today' : daysAgo(p.careNote.at, nowMs) + ' days ago') : '',
    firstMonth: p?.firstMonth ?? '',
    highlights: (p?.highlights ?? []).map((h) => {
      const n = daysAgo(h.at, nowMs);
      return { text: h.text, when: n === 0 ? 'today' : n === 1 ? 'yesterday afternoon' : n + ' days ago' };
    }),
    weekly: !!p?.weeklyEmail,
    coverage: (s.coverage?.facilities ?? []).map((f) => ({ name: f.name, residents: f.residents, represented: f.represented })),
    inbox: s.inbox
      ? {
        open: s.inbox.threads.filter((t) => t.awaitingReply).length,
        sla: s.inbox.repliedWithinSla ? s.inbox.repliedWithinSla.n + ' of ' + s.inbox.repliedWithinSla.of : DASH,
        routed: s.inbox.routedAsRequests == null ? DASH : String(s.inbox.routedAsRequests),
      }
      : null,
    photoSent: s.photoSent,
    photoNote: s.photoNote ?? null,
    todo: s.todo
      ? {
        askName: s.todo.askName,
        events: s.todo.events.map((e) => ({
          id: e.noticeId, kind: e.kind, title: e.title, redacted: e.redacted, seen: !!e.seenAt,
          when: notices.find((n) => n.id === e.noticeId)?.when ?? (e.when ? dateTime(e.when) : DASH),
          by: e.by ?? DASH,
          reason: e.stepsReason,
          steps: e.steps
            ? e.steps.map((st) => ({
              id: st.id, t: st.title, d: st.detail ?? '', act: st.action, kind: st.actionKind, facility: st.facilityFilled,
              // "Confirm you've seen it" is done once the notice is marked seen, however that happened.
              done: !!st.doneAt || (st.actionKind === 'ack' && !!e.seenAt),
            }))
            : null,
        })),
        nudges: s.todo.nudges.map((n) => ({
          key: n.key, kind: n.kind, who: n.who, days: n.days, label: n.label,
          date: n.month && n.day ? MON[n.month - 1] + ' ' + n.day : null, doneAt: n.doneAt ? timeOf(n.doneAt) : null,
        })),
      }
      : null,
  };
}

/** Her day-to-day wellbeing, 0-5: the shared mood trend (dashboard, 0-1) for a family, else the portal's. */
function moodFrom(s: FpSources): number[] {
  const pts = s.dashboard?.dashboard.find((d) => d.resident.id === s.resident?.id)?.moodTrend?.points;
  if (pts && pts.length) return pts.map((v) => Math.round(v * 50) / 10);
  return s.portal?.moodSeries ?? [];
}
